﻿import {DependencyList, useRef} from "react";

/**
 * Custom hook to run an initialization function with dependency tracking.
 * The provided function will only execute if it hasn't run yet or if the dependencies changed.
 *
 * @param {() => void} fn - The initialization function to be called when criteria are met.
 * @param {DependencyList} deps - Array of dependencies to track for changes.
 * @return {void} This hook does not return a value.
 */
export function useInitialize(fn: () => void, deps: DependencyList): void {
	const hasRun = useRef(false);
	const prevDeps = useRef<DependencyList>(undefined);

	const depsChanged = !prevDeps.current ||
		deps.some((dep, i) => !Object.is(dep, prevDeps.current![i]));

	if (!(!hasRun.current || depsChanged)) {
		return;
	}

	hasRun.current = true;
	prevDeps.current = deps;
	fn();
}
